// D288: the ONE rule that decides whether a game rates — the user's, verbatim in spirit:
// "the game is ranked if it starts with at least two humans present; bots may fill up empty
// seats", and "the only reason to unrank a game is if it's all bots".
//
// A HUMAN is any seat that is not one of our bots: signed-in or guest alike. Map, speed, VP
// target, discard limit, a scenario or a pasted map no longer un-rank anything (they did
// through H20 — T-RANK-021/022, Q14). It lives in shared because three places must give the
// same answer — the lobby's live checkmark, the play panel's card, and the server at START —
// and a second copy of a rule is a copy that can disagree (D198).

/** How many distinct humans a table needs, at start, to rate. */
export const RANKED_MIN_HUMANS = 2;

export interface RankVerdict {
  ranked: boolean;
  /** The distinct humans counted (one person in two tabs is one human). */
  humans: number;
  /** Plain words for the ⓘ tooltip — why this table does or does not rate. */
  reason: string;
}

export function rankVerdict(humans: number): RankVerdict {
  const ranked = humans >= RANKED_MIN_HUMANS;
  return { ranked, humans, reason: rankReason(ranked, humans) };
}

function rankReason(ranked: boolean, humans: number): string {
  if (ranked) return `Ranked: ${humans} human players are at the table. Bots may fill the empty seats; only the humans' results move ratings.`;
  if (humans <= 0) return 'Unranked: nobody but bots — a game needs at least two human players to count.';
  return 'Unranked: a game needs at least two human players to count. Bots can fill the other seats.';
}

/** One seated player as the ranked count sees it (built server-side, from the session). */
export interface RankSeat {
  /** A stable identity key — account id, else guest browser id, else per-connection — or null for a bot. */
  readonly identity: string | null;
}

/**
 * Count the distinct humans a table holds, for the ranked verdict: one per distinct identity.
 *
 * The dedupe has always mattered: the same account (or the same guest browser) in two seats is
 * ONE person, and H6-S4 measured what rating a player against themselves does (a free +162).
 *
 * D337 (the user's rule): a guest is one human per BROWSER, wherever it connects from. D299 had
 * collapsed guests that shared an address, which meant a LAN party, or two phones on one home
 * Wi-Fi, could never rate — and it also stopped those tables ending when one player resigned,
 * because the last-player ending was gated on the verdict. An address cannot tell two identical
 * devices behind one router from one device running two browsers, so it is not read at all.
 * What that gives up: one person with two browsers on one machine can rate against themselves —
 * which they could equally do with a phone and a laptop, and the rule must let those rate.
 */
export function countRankedHumans(seats: readonly RankSeat[]): number {
  return new Set(seats.map((s) => s.identity).filter((id): id is string => id !== null)).size;
}
